// src/hooks/useWebSocket.ts

import { useState, useEffect, useRef, useCallback } from "react";

export interface UseWebSocketOptions {
  onMessage?: (event: MessageEvent) => void;
  onCloseOrError?: () => void;
}

// ✅ Aggiorna il tipo di ritorno a un array
export const useWebSocket = (
  url: string,
  options?: UseWebSocketOptions
): [WebSocket | null, boolean] => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const errorGracePeriodRef = useRef<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const isMountedRef = useRef(true);
  const isBackgroundRef = useRef(false);
  const lastConnectedTimeRef = useRef<number>(0);

  const onMessageRef = useRef(options?.onMessage);
  const onCloseOrErrorRef = useRef(options?.onCloseOrError);

  // Grace period before showing error (5 seconds)
  const ERROR_GRACE_PERIOD = 5000;
  // Fast reconnect when coming from background
  const FAST_RECONNECT_DELAY = 500;
  // Normal reconnect delay
  const NORMAL_RECONNECT_DELAY = 3000;

  useEffect(() => {
    onMessageRef.current = options?.onMessage;
  }, [options?.onMessage]);

  useEffect(() => {
    onCloseOrErrorRef.current = options?.onCloseOrError;
  }, [options?.onCloseOrError]);

  const clearAllTimeouts = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (errorGracePeriodRef.current) {
      window.clearTimeout(errorGracePeriodRef.current);
      errorGracePeriodRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!isMountedRef.current) return;
    
    // Don't connect if URL is null or empty
    if (!url || url.trim() === "") {
      console.log("⏸️ WebSocket connection skipped: URL not ready");
      return;
    }

    // Close existing socket if any
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      console.log("🔄 Closing existing connection before reconnecting");
      socketRef.current.close();
    }

    console.log("🔌 Connecting to WebSocket:", url);
    const ws = new WebSocket(url);
    socketRef.current = ws;
    const thisSocket = ws;

    ws.onopen = () => {
      if (socketRef.current !== thisSocket) return;
      if (!isMountedRef.current) return;
      console.log("✅ WebSocket connected");
      setSocket(ws);
      setIsConnected(true);
      lastConnectedTimeRef.current = Date.now();
      
      // Clear any pending error grace period since we're connected now
      if (errorGracePeriodRef.current) {
        window.clearTimeout(errorGracePeriodRef.current);
        errorGracePeriodRef.current = null;
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      if (socketRef.current !== thisSocket) return;
      if (!isMountedRef.current) return;
      if (onMessageRef.current) {
        onMessageRef.current(event);
      }
    };

    ws.onerror = (error) => {
      if (socketRef.current !== thisSocket) return;
      if (!isMountedRef.current) return;
      console.error("⚠️ WebSocket error:", error);
      // Don't immediately show error - let onclose handle reconnection
    };

    ws.onclose = () => {
      if (socketRef.current !== thisSocket) return;
      if (!isMountedRef.current) return;
      
      const wasConnected = isConnected;
      setSocket(null);
      setIsConnected(false);
      
      // Determine reconnect delay based on whether app is in background
      const reconnectDelay = isBackgroundRef.current ? FAST_RECONNECT_DELAY : NORMAL_RECONNECT_DELAY;
      console.warn(`🔌 WebSocket closed – reconnecting in ${reconnectDelay}ms`);
      
      // Only trigger error callback after grace period and if not coming from background
      if (wasConnected && !isBackgroundRef.current) {
        // Start grace period before showing error
        if (!errorGracePeriodRef.current) {
          errorGracePeriodRef.current = window.setTimeout(() => {
            // Only show error if still not connected after grace period
            if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
              if (onCloseOrErrorRef.current) {
                onCloseOrErrorRef.current();
              }
            }
            errorGracePeriodRef.current = null;
          }, ERROR_GRACE_PERIOD);
        }
      }
      
      // Schedule reconnection
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, reconnectDelay);
    };
  }, [url, isConnected]);

  // Handle page visibility changes (app going to background/foreground)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App going to background
        isBackgroundRef.current = true;
        console.log("📱 App went to background");
      } else {
        // App coming to foreground
        console.log("📱 App returned to foreground");
        isBackgroundRef.current = false;
        
        // Clear any pending error display since user just returned
        if (errorGracePeriodRef.current) {
          window.clearTimeout(errorGracePeriodRef.current);
          errorGracePeriodRef.current = null;
        }
        
        // Check if socket needs reconnection
        const socketState = socketRef.current?.readyState;
        if (!socketRef.current || socketState === WebSocket.CLOSED || socketState === WebSocket.CLOSING) {
          console.log("🔄 Reconnecting after returning from background");
          clearAllTimeouts();
          // Immediate reconnection when returning from background
          connect();
        } else if (socketState === WebSocket.OPEN) {
          // Socket still open, ensure state is correct
          setIsConnected(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [connect, clearAllTimeouts]);

  useEffect(() => {
    isMountedRef.current = true;
    connect();
    
    return () => {
      isMountedRef.current = false;
      clearAllTimeouts();
      // Delay closing to avoid premature closure during React Strict Mode double-mount
      setTimeout(() => {
        if (socketRef.current && !isMountedRef.current) {
          socketRef.current.close();
        }
      }, 100);
    };
  }, [connect, clearAllTimeouts]);

  // ✅ Restituisci l'array con il socket e lo stato di connessione
  return [socket, isConnected];
};